import PlayerIdentity from "../services/PlayerIdentity.js";
import AccountService from "../services/AccountService.js";
import { formatDuration } from "./TimeUtils.js";
import { isRankedGame } from "./gameGroups.js";

export default class ScoreUtils {
    /**
     * Renders the HTML for a game mode item (e.g. for the scores page or end-game messages).
     * @param {string} modeLabel - Translated label for the mode (e.g. "Classic").
     * @param {number} currentScore - Current best score or progress.
     * @param {number} maxScore - Total number of items to find.
     * @param {number} gamesPlayed - Total number of games played.
     * @param {number} cumulativeScore - Total score across all games.
     * @param {string} modeClass - CSS class for color coding (e.g. "classic", "hard", "speed-run").
     * @param {string} [scoreKey] - Optional key for reset button (if specified, a reset button is shown).
     * @param {string} [gameId] - Optional game ID for reset button.
     * @returns {string} HTML string.
     */
    static renderModeStats(modeLabel, currentScore, maxScore, gamesPlayed, cumulativeScore, modeClass, scoreKey = null, gameId = null, options = {}) {
        const hideProgress = options.hideProgress || modeClass === 'speed-run';
        const hideDenominatorInAverage = options.hideDenominatorInAverage || modeClass === 'speed-run';
        const hideGamesPlayed = options.hideGamesPlayed || false;
        const hideAverage = options.hideAverage || false;
        const alwaysShow = options.alwaysShow || false;
        const hasData = alwaysShow || gamesPlayed > 0 || (currentScore !== null && currentScore > 0);

        // Clamp to maxScore: a stale/out-of-date stored value (e.g. recorded against
        // a larger country pool before a dataset change) must never display as
        // "over 100%" — a found/total count can't exceed its own total.
        const clampedScore = currentScore !== null && maxScore > 0 ? Math.min(currentScore, maxScore) : currentScore;
        const progressPercent = maxScore > 0 ? Math.round((clampedScore / maxScore) * 100) : 0;
        const avgScore = gamesPlayed > 0 ? Math.round(cumulativeScore / gamesPlayed) : 0;
        const lang = window.geoNerdApp.languageManager;

        let resetBtnHtml = "";
        if (scoreKey && hasData) {
            resetBtnHtml = `
                <button class="btn-reset-mode" data-game-id="${gameId}" data-mode-key="${scoreKey}" title="${lang.t("scores.reset")}">
                    <div class="icon-reset"></div>
                </button>
            `;
        }

        if (!hasData) {
            const noScoreLabel = lang.t("scores.noScore");
            return `
                <div class="mode-item ${modeClass} empty">
                    <div class="mode-header">
                        <span class="mode-label">${modeLabel}</span>
                        <span class="mode-value no-score">${noScoreLabel}</span>
                    </div>
                </div>
            `;
        }

        const safeScore = clampedScore !== null ? clampedScore : 0;
        let scoreDisplay = "";
        if (hideDenominatorInAverage) {
            scoreDisplay = `${safeScore}`;
        } else {
            scoreDisplay = `${safeScore}/${maxScore}`;
        }

        let progressHtml = "";
        if (!hideProgress && scoreKey) {
            progressHtml = `
                <div class="stat-progress">
                    <span class="stat-label">${lang.t("scores.progression")}</span>
                    <div class="progress-bar-container">
                        <div class="progress-bar" style="width: ${progressPercent}%"></div>
                    </div>
                    <span class="stat-value">${clampedScore !== null ? `${clampedScore}/${maxScore}` : `${progressPercent}%`}</span>
                </div>
            `;
        }

        // Lignes propres à l'écran de fin (temps du run, meilleur score) : elles
        // passent en tête, avant les cumuls, parce qu'elles parlent de la partie
        // qui vient de se jouer.
        const extraRowsHtml = (options.extraRows || [])
            .map(row => `
                <div class="stat-row">
                    <span class="stat-label">${row.label}</span>
                    <span class="stat-value">${row.value}</span>
                </div>
            `).join("");

        let gamesPlayedHtml = "";
        if (!hideGamesPlayed) {
            gamesPlayedHtml = `
                <div class="stat-row">
                    <span class="stat-label">${lang.t("scores.gamesPlayed")}</span>
                    <span class="stat-value">${gamesPlayed}</span>
                </div>
            `;
        }

        let averageScoreHtml = "";
        if (!hideAverage) {
            const displayAvg = gamesPlayed > 0 ? (hideDenominatorInAverage ? `${avgScore}` : `${avgScore}/${maxScore}`) : '-';
            averageScoreHtml = `
                <div class="stat-row">
                    <span class="stat-label">${lang.t("scores.averageScore")}</span>
                    <span class="stat-value">${displayAvg}</span>
                </div>
            `;
        }

        const hasDetails = extraRowsHtml || progressHtml || gamesPlayedHtml || averageScoreHtml;
        const isCollapsible = hasDetails && scoreKey;

        return `
            <div class="mode-item ${modeClass} has-data ${isCollapsible ? 'collapsible' : ''}">
                <div class="mode-header">
                    <span class="mode-label">${modeLabel}</span>
                    <span class="mode-value">${scoreDisplay}</span>
                    ${resetBtnHtml}
                </div>
                ${hasDetails ? `<div class="mode-stats">
                    ${extraRowsHtml}
                    ${progressHtml}
                    ${gamesPlayedHtml}
                    ${averageScoreHtml}
                </div>` : ''}
            </div>
        `;
    }

    /**
     * Saves statistics into localStorage.
     * @param {string} baseKey - Key prefix for the game mode (e.g. "flagnerd").
     * @param {number} score - Score to add to stats.
     */
    static saveStats(baseKey, score) {
        PlayerIdentity.registerFirstCompletion();

        const lang = window.geoNerdApp.languageManager;
        const gamesPlayedKey = lang.getStorageKey(`${baseKey}.gamesPlayed`);
        const cumulativeScoreKey = lang.getStorageKey(`${baseKey}.cumulativeScore`);

        const gamesPlayed = parseInt(localStorage.getItem(gamesPlayedKey) || "0") + 1;
        const cumulativeScore = parseInt(localStorage.getItem(cumulativeScoreKey) || "0") + score;

        localStorage.setItem(gamesPlayedKey, gamesPlayed.toString());
        localStorage.setItem(cumulativeScoreKey, cumulativeScore.toString());

        this.recordPlay(baseKey, score);
    }

    /**
     * Compte la partie côté serveur (`POST /plays`) : la seule trace datée de
     * l'activité par mode, pour la vue « 7 derniers jours » du back-office. Les
     * compteurs ci-dessus restent locaux et cumulés. Sans compte comme avec,
     * par l'identifiant d'appareil ; sans attente ni erreur visible hors ligne.
     * @param {string} mode - gameId (un mode inconnu du back-office est ignoré par le serveur).
     * @param {number} [score] - absent pour Country Nerd, qui compte des lettres.
     */
    static recordPlay(mode, score) {
        const body = { mode, device: PlayerIdentity.getOrCreateId() };
        if (Number.isInteger(score) && score >= 0) body.score = score;
        AccountService.request("POST", "/plays", body).catch(() => {});
    }

    static getStats(baseKey) {
        const lang = window.geoNerdApp.languageManager;
        return {
            gamesPlayed: parseInt(localStorage.getItem(lang.getStorageKey(`${baseKey}.gamesPlayed`)) || "0"),
            cumulativeScore: parseInt(localStorage.getItem(lang.getStorageKey(`${baseKey}.cumulativeScore`)) || "0")
        };
    }
    /**
     * Renders and injects stats into an end-game message container.
     * @param {HTMLElement} container - The win or lose message container.
     * @param {string} baseKey - Key prefix for stats.
     * @param {string} modeLabel - Translated label for the mode.
     * @param {number} score - Current game score.
     * @param {number} maxScore - Maximum possible score.
     * @param {string} modeClass - CSS class for styling.
     * @param {object} [options] - `{ duration, best, isNewBest }` : le temps mis par
     *   la partie qui vient de finir et le meilleur score du mode. Les deux étaient
     *   mesurés et classés sans jamais être montrés au joueur.
     */
    static renderEndGameResults(container, baseKey, modeLabel, score, maxScore, modeClass, options = {}) {
        const lang = window.geoNerdApp.languageManager;
        const stats = this.getStats(baseKey);

        const extraRows = [];
        if (typeof options.duration === "number" && options.duration > 0) {
            extraRows.push({ label: lang.t("game.elapsedTime"), value: formatDuration(options.duration) });
        }
        if (typeof options.best === "number" && options.best > 0) {
            const suffix = options.isNewBest ? ` 🏆 ${lang.t("game.newBest")}` : "";
            const best = maxScore > 0 && modeClass !== "speed-run"
                ? `${Math.min(options.best, maxScore)}/${maxScore}`
                : `${options.best}`;
            extraRows.push({ label: lang.t("game.bestScore"), value: `${best}${suffix}` });
        }

        const statsHtml = this.renderModeStats(
            modeLabel,
            score,
            maxScore,
            stats.gamesPlayed,
            stats.cumulativeScore,
            modeClass,
            null,
            null,
            { extraRows }
        );

        const existing = container.querySelector(".mode-item");
        if (existing) existing.remove();

        const messageP = container.querySelector("p");
        if (messageP) {
            messageP.insertAdjacentHTML('afterend', statsHtml);
        }

        const existingLink = container.querySelector(".btn-leaderboard");
        if (existingLink) existingLink.remove();
        if (!isRankedGame(baseKey)) return;

        const isEn = window.location.pathname.startsWith('/en');
        const href = `${isEn ? '/en' : ''}/leaderboard#${baseKey}`;
        const linkHtml = `<a href="${href}" class="btn btn-leaderboard"><span class="leaderboard-icon"></span>${lang.t("leaderboard.viewLeaderboard")}</a>`;
        const reloadBtn = container.querySelector(".reload");
        if (reloadBtn) {
            reloadBtn.insertAdjacentHTML('afterend', linkHtml);
        } else {
            container.insertAdjacentHTML('beforeend', linkHtml);
        }
    }
}
